import PropTypes from 'prop-types'
import {Flex, Button, Text} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {useState, useEffect} from 'react'
import useSWR from 'swr'

import {
  ExpenseAccountSandwichList,
  CreateExpenseAccountForm,
  ChooseExpenseAccountForm,
} from '../components'
import DropdownRadioCards from '../../DropdownRadioCards'
import {useRails} from '../../../contexts/rails'
import {fetcher} from '../../../../api'

const SelectAccountScreen = ({
  onCreateExpenseAccount,
  goToNextStep,
  handleClose,
  createError,
}) => {
  const {t} = useTranslation('connectExpenseAccount')
  const [selectedOption, setSelectedOption] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRadioChange = (event) => {
    setSelectedOption(event.target.value)
  }

  const handleCreateFormSubmit = async (values) => {
    setIsSubmitting(true)

    await onCreateExpenseAccount({
      name: values.name,
      description: values.description,
    })
    setIsSubmitting(false)
  }
  const {organisation, accountingProvider} = useRails()

  const {data: expenseAccountsData, isLoading} = useSWR(
    `/api/suppliers/${organisation.currentSupplier.id}/expense_accounts`,
    fetcher,
  )

  const mappedData =
    expenseAccountsData &&
    expenseAccountsData.expense_accounts &&
    expenseAccountsData.expense_accounts.length
      ? expenseAccountsData.expense_accounts.map((account) => {
          return {
            label: account.Code
              ? `${account.Name} (${account.Code})`
              : account.Name,
            value: account.AccountID,
          }
        })
      : []

  // Update form validity when selectedOption or selectedAccount changes
  useEffect(() => {
    if (selectedOption === 'choose') {
      // For existing account, check if account is selected
      setIsFormValid(!!selectedAccount)
    } else if (selectedOption === 'create') {
      // For new account, form validity is handled by the create form
      // We'll assume it's valid when the option is selected
      setIsFormValid(true)
    } else {
      setIsFormValid(false)
    }
  }, [selectedOption, selectedAccount])

  const radioOptions = [
    {
      value: 'choose',
      title: t('modal.selectAccount.radioGroup.existingAccount'),
      children: (
        <ChooseExpenseAccountForm
          data={mappedData}
          isLoading={isLoading}
          onSuccess={goToNextStep}
          onAccountChange={setSelectedAccount}
          onSubmittingChange={setIsSubmitting}
        />
      ),
    },
    {
      value: 'create',
      title: t('modal.selectAccount.radioGroup.newAccount'),
      children: (
        <CreateExpenseAccountForm
          onSubmit={handleCreateFormSubmit}
          error={createError}
        />
      ),
    },
  ]

  return (
    <Flex justify="center">
      <Flex flexDirection="column" width="400px" gap={6} pt={10}>
        {/* Title section */}
        <Flex flexDirection="column" gap={6}>
          <Text
            color="black"
            fontWeight="regular"
            fontSize="2xl"
            lineHeight="90%"
          >
            {t('modal.selectAccount.title')}
          </Text>
          <Text color="black" fontSize="14px" lineHeight="125%">
            {t('modal.selectAccount.description', {
              accounting_provider: accountingProvider.name,
            })}
          </Text>
        </Flex>

        <DropdownRadioCards
          options={radioOptions}
          value={selectedOption}
          onChange={handleRadioChange}
        />

        <ExpenseAccountSandwichList />

        <Flex gap={3} pt={2} mb={8}>
          <Button
            type="submit"
            form={
              selectedOption === 'create'
                ? 'create-expense-account-form'
                : 'choose-expense-account-form'
            }
            variant="primary"
            isDisabled={!isFormValid || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('buttons.submit')}
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            {t('buttons.cancel')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

SelectAccountScreen.propTypes = {
  onCreateExpenseAccount: PropTypes.func.isRequired,
  goToNextStep: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  createError: PropTypes.string,
}

SelectAccountScreen.defaultProps = {
  createError: null,
}

export default SelectAccountScreen
